import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from './api';

function MfaSection() {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    authApi.me().then((data) => setEnabled(!!data.user?.totpEnabled));
  }, []);

  const startSetup = async () => {
    setError(null);
    try {
      setSetup(await authApi.setupMfa());
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { backupCodes: codes } = await authApi.enableMfa(code);
      setBackupCodes(codes);
      setEnabled(true);
      setSetup(null);
      setCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.disableMfa(disableCode);
      setEnabled(false);
      setBackupCodes(null);
      setDisableCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (enabled === null) return null;

  if (backupCodes) {
    return (
      <div className="settings-section">
        <h2>Two-factor authentication enabled</h2>
        <p>Save these backup codes somewhere safe. Each can be used once if you lose access to your authenticator app.</p>
        <pre className="backup-codes">{backupCodes.join('\n')}</pre>
        <button className="primary-button" onClick={() => setBackupCodes(null)}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <h2>Two-factor authentication</h2>
      {enabled ? (
        <>
          <p>Two-factor authentication is currently enabled on your account.</p>
          <form className="auth-form" onSubmit={handleDisable}>
            <div className="input-group">
              <label>Enter a code to disable</label>
              <input
                type="text"
                inputMode="numeric"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                required
              />
            </div>
            {error && <div className="error-box">{error}</div>}
            <button type="submit" className="danger-link" disabled={loading}>
              Disable two-factor authentication
            </button>
          </form>
        </>
      ) : setup ? (
        <form className="auth-form" onSubmit={confirmEnable}>
          <p>Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.</p>
          <img src={setup.qrDataUrl} alt="MFA QR code" className="mfa-qr" />
          <p className="input-hint">Or enter this code manually: {setup.secret}</p>
          <div className="input-group">
            <label>Confirmation code</label>
            <input type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
          </div>
          {error && <div className="error-box">{error}</div>}
          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? 'Confirming…' : 'Enable two-factor authentication'}
          </button>
        </form>
      ) : (
        <>
          <p>Add an extra layer of security: a 6-digit code from an authenticator app will be required at login.</p>
          {error && <div className="error-box">{error}</div>}
          <button className="primary-button" onClick={startSetup}>
            Set up two-factor authentication
          </button>
        </>
      )}
    </div>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <h2>Change password</h2>
      <p>Changing your password signs you out of all other devices.</p>
      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <label>Current password</label>
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
        </div>
        <div className="input-group">
          <label>New password</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
        </div>
        {error && <div className="error-box">{error}</div>}
        {success && <div className="success-box">Password changed. Other sessions have been signed out.</div>}
        <button type="submit" className="primary-button" disabled={loading}>
          {loading ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}

function SessionsSection() {
  const [sessions, setSessions] = useState(null);
  const [error, setError] = useState(null);

  const refresh = () => authApi.listSessions().then(setSessions).catch((err) => setError(err.message));

  useEffect(() => {
    refresh();
  }, []);

  const revoke = async (id) => {
    await authApi.revokeSession(id);
    refresh();
  };

  const revokeOthers = async () => {
    await authApi.revokeOtherSessions();
    refresh();
  };

  return (
    <div className="settings-section">
      <h2>Active sessions</h2>
      {error && <div className="error-box">{error}</div>}
      {sessions?.length > 1 && (
        <button className="secondary-button" onClick={revokeOthers}>
          Sign out all other sessions
        </button>
      )}
      <ul className="session-list">
        {sessions?.map((s) => (
          <li key={s.id} className="session-row">
            <div>
              <div>{s.userAgent || 'Unknown device'}</div>
              <small className="input-hint">
                {s.ipAddress} · last active {new Date(s.lastSeenAt).toLocaleString()}
                {s.isCurrent && ' · this device'}
              </small>
            </div>
            {!s.isCurrent && (
              <button className="danger-link" onClick={() => revoke(s.id)}>
                Sign out
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivitySection() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    authApi.activity().then(setEvents).catch(() => setEvents([]));
  }, []);

  return (
    <div className="settings-section">
      <h2>Recent account activity</h2>
      <ul className="activity-list">
        {events?.map((e) => (
          <li key={e.id} className="activity-row">
            <span>{e.eventType.replace(/_/g, ' ')}</span>
            <small className="input-hint">{new Date(e.createdAt).toLocaleString()}</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SecuritySettings() {
  const navigate = useNavigate();

  return (
    <div className="auth-screen">
      <div className="auth-card settings-card">
        <div className="auth-brand">QuantumTrade</div>
        <h1 className="auth-title">Security settings</h1>

        <MfaSection />
        <PasswordSection />
        <SessionsSection />
        <ActivitySection />

        <button className="secondary-button" onClick={() => navigate('/')}>
          Back to dashboard
        </button>
      </div>
    </div>
  );
}
